import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, CheckCircle, AlertTriangle, XCircle, ArrowLeft, ArrowRight, Loader2, MapPin, Package, Info, Link2 } from 'lucide-react';
import { inventoryImportService, InventoryImportResponse, CsvHeaderExtractionResponse, ColumnMappings, DefaultValues } from '../../../../services/inventoryImportService';
import { warehouseService } from '../../../../services/warehouseService';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface InventoryImportProps {
  onBack?: () => void;
}

type Step = 'upload' | 'mapping' | 'preview' | 'result';

interface WarehouseOption {
  value: string;
  label: string;
}

export const InventoryImport: React.FC<InventoryImportProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [warehouseId, setWarehouseId] = useState('');
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [balanceStrategy, setBalanceStrategy] = useState<'replace' | 'add'>('replace');
  const [delimiter, setDelimiter] = useState('auto');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<InventoryImportResponse | null>(null);
  const [importResult, setImportResult] = useState<InventoryImportResponse | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<CsvHeaderExtractionResponse | null>(null);
  const [columnMappings, setColumnMappings] = useState<ColumnMappings>({});
  const [defaultValues, setDefaultValues] = useState<DefaultValues>({});
  const [skipInvalidLines, setSkipInvalidLines] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load warehouses on mount
  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const options = await warehouseService.getWarehouseOptions();
        setWarehouses(options.map(o => ({ value: o.value, label: o.label })));
        if (options.length > 0) {
          setWarehouseId(options[0].value);
        }
      } catch (err) {
        console.error('Failed to load warehouses:', err);
      }
    };
    loadWarehouses();
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      console.log('[InventoryImport] handleFileSelect triggered');
      const selected = e.target.files?.[0];
      console.log('[InventoryImport] Selected file:', selected?.name, selected?.size);
      if (selected) {
        setFile(selected);
        setError(null);
        setPreviewResult(null);
        console.log('[InventoryImport] State updated successfully');
      }
    } catch (err) {
      console.error('[InventoryImport] Error in handleFileSelect:', err);
      setError(t('manager.modules.adminHost.inventoryImport.fileSelectError', { '0': err instanceof Error ? err.message : String(err) }, `Feil ved valg av fil: ${err instanceof Error ? err.message : String(err)}`));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files[0];
    if (dropped && (dropped.name.endsWith('.csv') || dropped.name.endsWith('.txt'))) {
      setFile(dropped);
      setError(null);
      setPreviewResult(null);
    } else {
      setError(t('manager.modules.adminHost.inventoryImport.csvTxtOnly', {}, 'Kun CSV- og TXT-filer er st\u00f8ttet.'));
    }
  };

  const handleExtractHeaders = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const headersData = await inventoryImportService.extractCsvHeaders(file, delimiter);
      setCsvHeaders(headersData);

      // Initialize mappings with auto-suggested values
      const initialMappings: ColumnMappings = {};
      Object.entries(headersData.suggestedMappings).forEach(([target, csvCol]) => {
        initialMappings[target] = csvCol;
      });
      setColumnMappings(initialMappings);
      setStep('mapping');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateFromMapping = async (withSkip: boolean = false) => {
    if (!file || !warehouseId) return;

    setLoading(true);
    setError(null);

    const skip = withSkip || skipInvalidLines;
    if (withSkip) setSkipInvalidLines(true);

    try {
      const result = await inventoryImportService.validateCsv(file, warehouseId, balanceStrategy, delimiter, columnMappings, defaultValues, skip);
      setPreviewResult(result);

      // Go to preview if no errors, or if we're skipping invalid lines and the import succeeded
      if (result.validationErrors.length === 0 || (skip && result.success)) {
        setStep('preview');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file || !warehouseId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await inventoryImportService.importCsv(file, warehouseId, balanceStrategy, false, delimiter, columnMappings, defaultValues, skipInvalidLines);
      setImportResult(result);
      setStep('result');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (targetField: string, csvColumn: string | null) => {
    setColumnMappings(prev => ({
      ...prev,
      [targetField]: csvColumn
    }));
  };

  const getSampleValues = (csvColumn: string): string => {
    if (!csvHeaders) return '';
    const colIndex = csvHeaders.headers.indexOf(csvColumn);
    if (colIndex < 0) return '';
    const samples = csvHeaders.sampleRows
      .map(row => row[colIndex])
      .filter(v => v && v.trim() !== '')
      .slice(0, 3);
    return samples.join(', ') || t('manager.modules.adminHost.inventoryImport.emptySample', {}, '(tom)');
  };

  const handleDefaultValueChange = (targetField: string, value: string) => {
    setDefaultValues(prev => {
      if (!value) {
        const { [targetField]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [targetField]: value };
    });
  };

  // Fields that support default values (exclude BinCode - always required from CSV, and ItemNo/Quantity - only relevant per row)
  const supportsDefaultValue = (field: string) =>
    !['BinCode', 'ItemNo', 'Quantity'].includes(field);

  // Placeholder hints for default value inputs
  const defaultValuePlaceholder: Record<string, string> = {
    ZoneCode: t('manager.modules.adminHost.inventoryImport.placeholderZoneCode', {}, 'f.eks. HOVED'),
    BinTypeNo: t('manager.modules.adminHost.inventoryImport.placeholderExample1', {}, 'f.eks. 1'),
    BinGrpNo: t('manager.modules.adminHost.inventoryImport.placeholderExample1', {}, 'f.eks. 1'),
    BinCatNo: t('manager.modules.adminHost.inventoryImport.placeholderExample1', {}, 'f.eks. 1'),
    Description: t('manager.modules.adminHost.inventoryImport.placeholderDescription', {}, 'f.eks. Importert lokasjon'),
    PickPriorityNo: t('manager.modules.adminHost.inventoryImport.placeholderExample5', {}, 'f.eks. 5'),
    PutAwayPriorityNo: t('manager.modules.adminHost.inventoryImport.placeholderExample5', {}, 'f.eks. 5'),
    SharedBin: 'true/false'
  };

  const isBinCodeMapped = !!columnMappings['BinCode'];

  const handleReset = () => {
    setStep('upload');
    setFile(null);
    setPreviewResult(null);
    setImportResult(null);
    setCsvHeaders(null);
    setColumnMappings({});
    setDefaultValues({});
    setSkipInvalidLines(false);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Back button */}
      {onBack && (
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('manager.modules.adminHost.inventoryImport.backToAdmin', {}, 'Tilbake til administrasjon')}
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-800 to-emerald-700 text-white p-6 rounded-lg">
        <div className="flex items-center gap-3 mb-2">
          <Upload className="w-8 h-8" />
          <h1 className="text-2xl font-bold">{t('manager.modules.adminHost.inventoryImport.title', {}, 'Saldo- og lokasjonsimport')}</h1>
        </div>
        <p className="text-emerald-200">{t('manager.modules.adminHost.inventoryImport.subtitle', {}, 'Importer lokasjoner og startsaldo fra CSV-fil for \u00e5 sette opp nytt lager')}</p>
      </div>

      {/* Progress steps */}
      <div className="flex items-center justify-center gap-3">
        {[
          { id: 'upload', label: t('manager.modules.adminHost.inventoryImport.steps.upload', {}, '1. Last opp'), icon: FileText },
          { id: 'mapping', label: t('manager.modules.adminHost.inventoryImport.steps.mapping', {}, '2. Koble kolonner'), icon: Link2 },
          { id: 'preview', label: t('manager.modules.adminHost.inventoryImport.steps.preview', {}, '3. Forhåndsvisning'), icon: CheckCircle },
          { id: 'result', label: t('manager.modules.adminHost.inventoryImport.steps.result', {}, '4. Resultat'), icon: Package },
        ].map((s, i) => {
          const Icon = s.icon;
          const isActive = s.id === step;
          const stepOrder = ['upload', 'mapping', 'preview', 'result'];
          const currentIndex = stepOrder.indexOf(step);
          const isDone = i < currentIndex;
          return (
            <React.Fragment key={s.id}>
              {i > 0 && <div className={`h-0.5 w-8 ${isDone ? 'bg-emerald-500' : 'bg-gray-300'}`} />}
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                isActive ? 'bg-emerald-100 text-emerald-800' :
                isDone ? 'bg-emerald-50 text-emerald-600' :
                'bg-gray-100 text-gray-500'
              }`}>
                <Icon className="w-4 h-4" />
                {s.label}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-red-800 font-medium">{t('manager.modules.adminHost.inventoryImport.errorLabel', {}, 'Feil')}</p>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div className="space-y-6">
          {/* Settings */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('manager.modules.adminHost.inventoryImport.settings', {}, 'Innstillinger')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Warehouse */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('warehouse.general.warehouse', {}, 'Lager')}</label>
                <select
                  value={warehouseId}
                  onChange={e => setWarehouseId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {warehouses.map(w => (
                    <option key={w.value} value={w.value}>{w.label}</option>
                  ))}
                </select>
              </div>

              {/* Balance strategy */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('manager.modules.adminHost.inventoryImport.balanceStrategy', {}, 'Saldo-strategi')}</label>
                <select
                  value={balanceStrategy}
                  onChange={e => setBalanceStrategy(e.target.value as 'replace' | 'add')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="replace">{t('manager.modules.adminHost.inventoryImport.strategyReplace', {}, 'Erstatt (sett ny verdi)')}</option>
                  <option value="add">{t('manager.modules.adminHost.inventoryImport.strategyAdd', {}, 'Legg til (adder til eksisterende)')}</option>
                </select>
              </div>

              {/* Delimiter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('manager.modules.adminHost.inventoryImport.delimiter', {}, 'Skilletegn')}</label>
                <select
                  value={delimiter}
                  onChange={e => setDelimiter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="auto">{t('manager.modules.adminHost.inventoryImport.delimiterAuto', {}, 'Auto-detect')}</option>
                  <option value="semicolon">{t('manager.modules.adminHost.inventoryImport.delimiterSemicolon', {}, 'Semikolon (;)')}</option>
                  <option value="comma">{t('manager.modules.adminHost.inventoryImport.delimiterComma', {}, 'Komma (,)')}</option>
                  <option value="tab">{t('manager.modules.adminHost.inventoryImport.delimiterTab', {}, 'Tab')}</option>
                  <option value="pipe">{t('manager.modules.adminHost.inventoryImport.delimiterPipe', {}, 'Pipe (|)')}</option>
                </select>
              </div>
            </div>
          </div>

          {/* File upload */}
          {/* Hidden file input — always in DOM, never removed */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            onChange={handleFileSelect}
            className="hidden"
          />
          <div
            className={`bg-white rounded-lg p-8 shadow-sm border-2 border-dashed transition-colors cursor-pointer ${
              file ? 'border-emerald-300 bg-emerald-50' : 'border-gray-300 hover:border-emerald-400'
            }`}
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => { if (!file) fileInputRef.current?.click(); }}
          >
            <div className="text-center">
              {file ? (
                <div className="space-y-3">
                  <FileText className="w-12 h-12 text-emerald-600 mx-auto" />
                  <div>
                    <p className="text-lg font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setFile(null); setPreviewResult(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    {t('manager.modules.adminHost.inventoryImport.removeFile', {}, 'Fjern fil')}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                  <div>
                    <p className="text-lg font-medium text-gray-700">{t('manager.modules.adminHost.inventoryImport.dropFileHere', {}, 'Dra og slipp CSV-fil her')}</p>
                    <p className="text-sm text-gray-500">{t('manager.modules.adminHost.inventoryImport.orClickToSelect', {}, 'eller klikk for å velge fil')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* CSV format guide */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-2">{t('manager.modules.adminHost.inventoryImport.expectedFormat', {}, 'Forventet CSV-format (semikolon-separert):')}</p>
                <code className="block bg-blue-100 rounded p-3 text-xs font-mono overflow-x-auto whitespace-pre">
{`BinCode;ZoneCode;BinTypeNo;Description;ItemNo;Quantity
MOTT-01;MOTTAK;2;Mottaksomr\u00e5de;;
A-01-01;PLUKK-A;1;Rad A Hylle 1;VARE-1001;250
A-01-02;PLUKK-A;1;Rad A Hylle 2;VARE-1002;120
B-01-01;PLUKK-B;1;Rad B Hylle 1;;`}
                </code>
                <p className="mt-2 text-blue-700">
                  {t('manager.modules.adminHost.inventoryImport.formatNote', {}, 'Linjer uten ItemNo oppretter kun lokasjoner. Linjer med ItemNo + Quantity oppretter ogs\u00e5 saldo.')}
                </p>
              </div>
            </div>
          </div>

          {/* Action button */}
          <div className="flex justify-end">
            <button
              onClick={handleExtractHeaders}
              disabled={!file || !warehouseId || loading}
              className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('manager.modules.adminHost.inventoryImport.readingFile', {}, 'Leser fil...')}
                </>
              ) : (
                <>
                  {t('manager.modules.adminHost.inventoryImport.nextMapColumns', {}, 'Neste - Koble kolonner')}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Column Mapping */}
      {step === 'mapping' && csvHeaders && (
        <div className="space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">{t('manager.modules.adminHost.inventoryImport.mapColumnsTitle', {}, 'Koble dine kolonner til systemfelter')}</p>
                <p>
                  {t('manager.modules.adminHost.inventoryImport.mapColumnsIntro', {}, 'Systemet har gjenkjent kolonnene i din CSV-fil. Sjekk at koblingene er riktige, og juster der det trengs. Felt merket med * er påkrevd.')}
                </p>
                <p className="mt-1">
                  {t('manager.modules.adminHost.inventoryImport.mapColumnsDefaultNote', {}, 'For felt som ikke finnes i filen kan du sette en standardverdi som brukes for alle rader.')}
                </p>
              </div>
            </div>
          </div>

          {/* BinCode warning */}
          {!isBinCodeMapped && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm text-yellow-800">
                <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                <span>{t('manager.modules.adminHost.inventoryImport.binCodeRequiredWarning', {}, 'Lokasjonskode (BinCode) er påkrevd men ikke koblet. Velg hvilken kolonne som inneholder lokasjonskoder.')}</span>
              </div>
            </div>
          )}

          {/* Mapping Grid */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('manager.modules.adminHost.inventoryImport.columnMappingTitle', {}, 'Kolonnetilordning')}</h3>

            <div className="space-y-3">
              {/* Header row */}
              <div className="grid grid-cols-12 gap-3 items-center pb-2 border-b border-gray-200">
                <div className="col-span-3 text-xs font-semibold text-gray-500 uppercase">{t('manager.modules.adminHost.inventoryImport.colSystemField', {}, 'Systemfelt')}</div>
                <div className="col-span-3 text-xs font-semibold text-gray-500 uppercase">{t('manager.modules.adminHost.inventoryImport.colYourCsvColumn', {}, 'Din CSV-kolonne')}</div>
                <div className="col-span-3 text-xs font-semibold text-gray-500 uppercase">{t('manager.modules.adminHost.inventoryImport.colSampleData', {}, 'Eksempeldata')}</div>
                <div className="col-span-3 text-xs font-semibold text-gray-500 uppercase">{t('manager.modules.adminHost.inventoryImport.colDefaultValue', {}, 'Standardverdi')}</div>
              </div>

              {Object.entries(csvHeaders.availableTargetFields).map(([targetField, label]) => {
                const currentMapping = columnMappings[targetField] || '';
                const isRequired = targetField === 'BinCode';
                const canHaveDefault = supportsDefaultValue(targetField);
                const currentDefault = defaultValues[targetField] || '';

                return (
                  <div key={targetField} className={`grid grid-cols-12 gap-3 items-center py-1.5 ${
                    isRequired && !currentMapping ? 'bg-yellow-50 -mx-2 px-2 rounded' : ''
                  }`}>
                    {/* Target field label */}
                    <div className="col-span-3">
                      <label className="text-sm font-medium text-gray-700">
                        {label}
                      </label>
                    </div>

                    {/* Dropdown for CSV column selection */}
                    <div className="col-span-3">
                      <select
                        value={currentMapping}
                        onChange={(e) => handleMappingChange(targetField, e.target.value || null)}
                        className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                          isRequired && !currentMapping
                            ? 'border-yellow-400 bg-yellow-50'
                            : currentMapping
                              ? 'border-emerald-300 bg-emerald-50'
                              : 'border-gray-300'
                        }`}
                      >
                        <option value="">{t('manager.modules.adminHost.inventoryImport.notMapped', {}, '-- Ikke koblet --')}</option>
                        {csvHeaders.headers.map((header, idx) => (
                          <option key={idx} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Sample data preview */}
                    <div className="col-span-3">
                      {currentMapping ? (
                        <div className="text-xs text-gray-600 font-mono bg-gray-50 px-2 py-1.5 rounded border border-gray-200 truncate">
                          {getSampleValues(currentMapping)}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400 italic px-2 py-1.5">
                          {canHaveDefault ? t('manager.modules.adminHost.inventoryImport.notMappedShort', {}, 'Ikke koblet') : t('manager.modules.adminHost.inventoryImport.selectColumn', {}, 'Velg en kolonne')}
                        </div>
                      )}
                    </div>

                    {/* Default value input */}
                    <div className="col-span-3">
                      {canHaveDefault && !currentMapping ? (
                        <input
                          type="text"
                          value={currentDefault}
                          onChange={(e) => handleDefaultValueChange(targetField, e.target.value)}
                          placeholder={defaultValuePlaceholder[targetField] || ''}
                          className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            currentDefault
                              ? 'border-blue-300 bg-blue-50'
                              : 'border-gray-300'
                          }`}
                        />
                      ) : canHaveDefault && currentMapping ? (
                        <div className="text-xs text-gray-400 italic px-2 py-1.5">
                          {t('manager.modules.adminHost.inventoryImport.usesCsvData', {}, 'Bruker CSV-data')}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-300 italic px-2 py-1.5">
                          —
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Default values info */}
            {Object.keys(defaultValues).length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <p className="text-xs text-blue-600">
                  {t('manager.modules.adminHost.inventoryImport.defaultsSetNote', { '0': Object.keys(defaultValues).length }, `${Object.keys(defaultValues).length} standardverdi(er) satt — brukes for alle rader der feltet ikke er koblet til en CSV-kolonne.`)}
                </p>
              </div>
            )}
          </div>

          {/* Sample Data Table */}
          {csvHeaders.sampleRows.length > 0 && (
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('manager.modules.adminHost.inventoryImport.sampleDataTitle', { '0': csvHeaders.sampleRows.length }, `Eksempeldata fra fil (${csvHeaders.sampleRows.length} rader)`)}
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">#</th>
                      {csvHeaders.headers.map((header, idx) => {
                        // Highlight mapped columns
                        const isMapped = Object.values(columnMappings).includes(header);
                        return (
                          <th key={idx} className={`px-3 py-2 text-left text-xs font-medium ${
                            isMapped ? 'text-emerald-700 bg-emerald-50' : 'text-gray-500'
                          }`}>
                            {header}
                            {isMapped && <span className="ml-1 text-emerald-500">✓</span>}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {csvHeaders.sampleRows.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        <td className="px-3 py-1.5 text-xs text-gray-400 font-mono">{rowIdx + 1}</td>
                        {row.map((cell, cellIdx) => {
                          const isMapped = Object.values(columnMappings).includes(csvHeaders.headers[cellIdx]);
                          return (
                            <td key={cellIdx} className={`px-3 py-1.5 text-xs font-mono ${
                              isMapped ? 'text-gray-900 bg-emerald-50' : 'text-gray-500'
                            }`}>
                              {cell || <span className="text-gray-300">-</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Validation errors from dry-run (shown if user came back) */}
          {previewResult && previewResult.validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <h3 className="font-medium text-red-800">
                    {t('manager.modules.adminHost.inventoryImport.validationErrorsTitle', { '0': previewResult.validationErrors.length }, `${previewResult.validationErrors.length} valideringsfeil`)}
                  </h3>
                </div>
                <button
                  onClick={() => handleValidateFromMapping(true)}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:bg-gray-300 transition-colors text-sm font-medium"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ArrowRight className="w-4 h-4" />
                  )}
                  {t('manager.modules.adminHost.inventoryImport.skipErrorLines', {}, 'Hopp over feillinjer og fortsett')}
                </button>
              </div>
              <p className="text-sm text-red-700 mb-2">
                {t('manager.modules.adminHost.inventoryImport.skippedLinesInfo', { '0': previewResult.locationsCreated + previewResult.locationsUpdated > 0
                  ? t('manager.modules.adminHost.inventoryImport.locationsCountSuffix', { '0': previewResult.locationsCreated + previewResult.locationsUpdated }, `${previewResult.locationsCreated + previewResult.locationsUpdated} lokasjoner`)
                  : t('manager.modules.adminHost.inventoryImport.validLines', {}, 'gyldige linjer') }, `Disse linjene vil bli hoppet over. Resten (${previewResult.locationsCreated + previewResult.locationsUpdated > 0
                  ? `${previewResult.locationsCreated + previewResult.locationsUpdated} lokasjoner`
                  : 'gyldige linjer'}) importeres.`)}
              </p>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {previewResult.validationErrors.map((err, i) => (
                  <div key={i} className="text-sm text-red-700 flex gap-2">
                    <span className="font-mono text-red-500 flex-shrink-0">{t('manager.modules.adminHost.inventoryImport.lineLabel', { '0': err.lineNumber }, `Linje ${err.lineNumber}:`)}</span>
                    <span>{err.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between">
            <button
              onClick={() => setStep('upload')}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('common.buttons.back', {}, 'Tilbake')}
            </button>
            <button
              onClick={() => handleValidateFromMapping()}
              disabled={!isBinCodeMapped || loading}
              className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('manager.modules.adminHost.inventoryImport.validating', {}, 'Validerer...')}
                </>
              ) : (
                <>
                  {t('manager.modules.adminHost.inventoryImport.validateAndPreview', {}, 'Valider og forhåndsvis')}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Preview */}
      {step === 'preview' && previewResult && (
        <div className="space-y-6">
          {/* Preview summary */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('manager.modules.adminHost.inventoryImport.importSummaryTitle', {}, 'Sammendrag av import')}</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Locations */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  <h3 className="font-medium text-blue-900">{t('manager.modules.adminHost.inventoryImport.locationsStcBin', {}, 'Lokasjoner (StcBin)')}</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-blue-700">{t('manager.modules.adminHost.inventoryImport.newLocations', {}, 'Nye lokasjoner:')}</span>
                    <span className="font-semibold text-blue-900">{previewResult.locationsCreated}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-700">{t('manager.modules.adminHost.inventoryImport.updated', {}, 'Oppdateres:')}</span>
                    <span className="font-semibold text-blue-900">{previewResult.locationsUpdated}</span>
                  </div>
                </div>
              </div>

              {/* Balances */}
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-5 h-5 text-green-600" />
                  <h3 className="font-medium text-green-900">{t('manager.modules.adminHost.inventoryImport.balancesItemBins', {}, 'Saldo (ItemBins)')}</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-green-700">{t('manager.modules.adminHost.inventoryImport.newBalanceRows', {}, 'Nye saldo-rader:')}</span>
                    <span className="font-semibold text-green-900">{previewResult.balancesCreated}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">{t('manager.modules.adminHost.inventoryImport.updated', {}, 'Oppdateres:')}</span>
                    <span className="font-semibold text-green-900">{previewResult.balancesUpdated}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-1">
              <div className="flex gap-2 text-sm text-gray-600">
                <span className="font-medium">{t('manager.modules.adminHost.inventoryImport.warehouseLabel', {}, 'Lager:')}</span>
                <span>{warehouseId}</span>
              </div>
              <div className="flex gap-2 text-sm text-gray-600">
                <span className="font-medium">{t('manager.modules.adminHost.inventoryImport.modeLabel', {}, 'Modus:')}</span>
                <span>{previewResult.warehouseMode}</span>
              </div>
              <div className="flex gap-2 text-sm text-gray-600">
                <span className="font-medium">{t('manager.modules.adminHost.inventoryImport.strategyLabel', {}, 'Strategi:')}</span>
                <span>{balanceStrategy === 'replace' ? t('manager.modules.adminHost.inventoryImport.strategyReplaceText', {}, 'Erstatt eksisterende saldo') : t('manager.modules.adminHost.inventoryImport.strategyAddText', {}, 'Legg til p\u00e5 eksisterende saldo')}</span>
              </div>
            </div>

            {/* Messages */}
            {previewResult.messages.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                {previewResult.messages.map((msg, i) => (
                  <p key={i} className="text-sm text-gray-600">{msg}</p>
                ))}
              </div>
            )}

            {/* Warnings */}
            {previewResult.warnings.length > 0 && (
              <div className="mt-4 bg-yellow-50 rounded-lg p-3 space-y-1">
                {previewResult.warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-yellow-800">
                    <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>{w}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Skipped lines detail (collapsed) */}
            {skipInvalidLines && previewResult.validationErrors.length > 0 && (
              <details className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <summary className="cursor-pointer text-sm font-medium text-amber-800">
                  {t('manager.modules.adminHost.inventoryImport.linesSkippedDetails', { '0': previewResult.validationErrors.length }, `${previewResult.validationErrors.length} linjer hoppet over — klikk for detaljer`)}
                </summary>
                <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                  {previewResult.validationErrors.map((err, i) => (
                    <div key={i} className="text-xs text-amber-700 flex gap-2">
                      <span className="font-mono flex-shrink-0">{t('manager.modules.adminHost.inventoryImport.lineLabel', { '0': err.lineNumber }, `Linje ${err.lineNumber}:`)}</span>
                      <span>{err.message}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex justify-between">
            <button
              onClick={() => setStep('mapping')}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('manager.modules.adminHost.inventoryImport.backToColumns', {}, 'Tilbake til kolonner')}
            </button>
            <button
              onClick={handleImport}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('manager.modules.adminHost.inventoryImport.importing', {}, 'Importerer...')}
                </>
              ) : (
                <>
                  {t('manager.modules.adminHost.inventoryImport.runImport', {}, 'Kjør import')}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Result */}
      {step === 'result' && importResult && (
        <div className="space-y-6">
          {/* Result header */}
          <div className={`rounded-lg p-6 ${
            importResult.success
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center gap-3 mb-2">
              {importResult.success ? (
                <CheckCircle className="w-8 h-8 text-green-600" />
              ) : (
                <XCircle className="w-8 h-8 text-red-600" />
              )}
              <h2 className={`text-xl font-bold ${importResult.success ? 'text-green-900' : 'text-red-900'}`}>
                {importResult.success ? t('manager.modules.adminHost.inventoryImport.importComplete', {}, 'Import fullf\u00f8rt') : t('manager.modules.adminHost.inventoryImport.importFailed', {}, 'Import feilet')}
              </h2>
            </div>
            {importResult.errorMessage && (
              <p className={`${importResult.success ? 'text-green-700' : 'text-red-700'}`}>
                {importResult.errorMessage}
              </p>
            )}
          </div>

          {/* Result details */}
          {importResult.success && (
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('manager.modules.adminHost.inventoryImport.resultTitle', {}, 'Resultat')}</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Locations */}
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <MapPin className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-900">
                    {importResult.locationsCreated + importResult.locationsUpdated}
                  </p>
                  <p className="text-sm text-blue-700">{t('manager.modules.adminHost.inventoryImport.locationsProcessed', {}, 'Lokasjoner behandlet')}</p>
                  <p className="text-xs text-blue-600 mt-1">
                    {t('manager.modules.adminHost.inventoryImport.locationsNewUpdated', { '0': importResult.locationsCreated, '1': importResult.locationsUpdated }, `${importResult.locationsCreated} nye, ${importResult.locationsUpdated} oppdatert`)}
                  </p>
                </div>

                {/* Balances */}
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <Package className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-900">
                    {importResult.balancesCreated + importResult.balancesUpdated}
                  </p>
                  <p className="text-sm text-green-700">{t('manager.modules.adminHost.inventoryImport.balanceRowsProcessed', {}, 'Saldo-rader behandlet')}</p>
                  <p className="text-xs text-green-600 mt-1">
                    {t('manager.modules.adminHost.inventoryImport.locationsNewUpdated', { '0': importResult.balancesCreated, '1': importResult.balancesUpdated }, `${importResult.balancesCreated} nye, ${importResult.balancesUpdated} oppdatert`)}
                  </p>
                </div>

                {/* Audit */}
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <FileText className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-purple-900">
                    {importResult.auditTransactionsCreated}
                  </p>
                  <p className="text-sm text-purple-700">{t('manager.modules.adminHost.inventoryImport.auditTransactions', {}, 'Audit-transaksjoner')}</p>
                  {importResult.auditBatchId && (
                    <p className="text-xs text-purple-600 mt-1 font-mono">
                      {importResult.auditBatchId}
                    </p>
                  )}
                </div>
              </div>

              {/* Messages */}
              {importResult.messages.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-1">
                  {importResult.messages.map((msg, i) => (
                    <p key={i} className="text-sm text-gray-600">{msg}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Validation errors */}
          {importResult.validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-medium text-red-800 mb-2">
                {t('manager.modules.adminHost.inventoryImport.validationErrorsTitle', { '0': importResult.validationErrors.length }, `${importResult.validationErrors.length} valideringsfeil`)}
              </h3>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {importResult.validationErrors.map((err, i) => (
                  <div key={i} className="text-sm text-red-700 flex gap-2">
                    <span className="font-mono text-red-500 flex-shrink-0">{t('manager.modules.adminHost.inventoryImport.lineLabel', { '0': err.lineNumber }, `Linje ${err.lineNumber}:`)}</span>
                    <span>{err.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
            >
              <Upload className="w-5 h-5" />
              {t('manager.modules.adminHost.inventoryImport.newImport', {}, 'Ny import')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
